import userStore from "../stores/userStore";
import { NETWORK_ERROR_STATUS } from "./helper";

const axios = require("axios");
export const axiosInstance = axios.create();

// Returned instead of an axios response when the request got no response at all
// (offline, DNS, CORS, timeout), so callers can always read status and data.
const noResponse = (error) => ({ status: NETWORK_ERROR_STATUS, data: {}, error });

class EndpointError extends Error {
  constructor(obj, msg) {
    super();
    this.name = "EndpointError";
    this.message = obj;
    console.error("EndpointError detail", obj, msg);
    if (typeof Error.captureStackTrace === "function") {
      Error.captureStackTrace(this, this.constructor);
    } else {
      this.stack = new Error(obj).stack;
    }
  }
}

class Fetcher {
  constructor(apis, endpoints) {
    this.apis = apis;
    this.endpoints = endpoints;
  }

  apis = {};
  defaultApi = undefined;
  endpoints = {};
  method = "GET";
  body = null;

  customOptions = {};

  get proxy() {
    const that = this;

    return new Proxy(that.endpoints, {
      get(target, prop) {
        that.defaultApi = apis[prop] || apis["ip"];
        if (!target[prop])
          throw new EndpointError("There isn't such endpoint", target[prop]);
        return new Proxy(target[prop], {
          get(subTarget, subProp) {
            if (!subTarget[subProp])
              throw new EndpointError(
                "There isn't such endpoint",
                target[prop]
              );
            return (...params) => {
              const all = [...params];
              return that.call(that.endpoints[prop][subProp](...all));
            };
          },
        });
      },
    });
  }

  get from() {
    return this.proxy;
  }
  get to() {
    return this.proxy;
  }

  call = async (endpoint) => {
    try {
      if (!this.defaultApi) return false;

      const url = `${this.defaultApi}/${endpoint}`.replace(
        /([^:]\/)\/+/g,
        "$1"
      );
      const body = this.body ? { data: this.body } : {};
      const options = {
        method: this.method,
        ...body,

        ...this.customOptions,
      };

      return await this.requestCall(url, options);
    } catch (e) {
      console.error("Fetcher call failed", endpoint, e);
      return noResponse(e);
    }
  };

  requestCall = async (url, options) => {
    try {
      const res = await axios({
        url: url,

        ...options,
      });
      return res;
    } catch (error) {
      if (!error?.response) {
        console.error("Request got no response", url, error);
        return noResponse(error);
      }
      if (!url.includes("login") && error.response.status === 401) {
        const res = await this.reCall(url);

        return res || noResponse();
      }
      return error.response;
    }
  };

  reCall = async (url) => {
    try {
      const sessionStorageUser = JSON.parse(
        localStorage.getItem("coralis-support-user")
      );
      const token = sessionStorageUser?.userDetails?.refreshToken;
      const tokenUrl = `${this.defaultApi}auth/token`.replace(
        /([^:]\/)\/+/g,
        "$1"
      );
      return await axios
        .post(tokenUrl, {
          refreshToken: token,
        })
        .then(async (response) => {
          const accessToken = response.data.accessToken;
          // Keep the whole session (not only userDetails): it's also read on page load and by
          // the other open tabs, which pick up the new token from it
          window.localStorage.setItem(
            "coralis-support-user",
            JSON.stringify({
              userDetails: {
                ...userStore.userDetails,
                accessToken: accessToken,
              },
              isAuthenticated: userStore.isAuthenticated,
              isCoralisEmployee: userStore.isCoralisEmployee,
            })
          );

          userStore.setUserDetails({
            ...userStore.userDetails,
            accessToken: accessToken,
          });
          this.setOptions({
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: "application/json",
              "Content-Type": "application/json; charset=utf-8",
            },
          });

          if (userStore.userDetails.accessToken && userStore.isAuthenticated) {
            const body = this.body ? { data: this.body } : {};
            const options = {
              method: this.method,
              ...body,

              ...this.customOptions,
            };

            const res = await this.requestCall(url, options);
            return res;
          }
        })
        .catch((error) => {
          console.error("Token refresh failed", error, error?.response);
          userStore.setError("error:error_jwt_expired");
          userStore.setIsAuthenticated(false);

          return error?.response || noResponse(error);
        });
    } catch (error) {
      console.error("Token refresh failed", error, error?.response);
      userStore.setIsAuthenticated(false);
      return error?.response || noResponse(error);
    }
  };

  setOptions = (customOptions) => {
    this.customOptions = customOptions;
    return this;
  };

  get get() {
    this.method = "GET";
    this.body = null;
    return this;
  }

  post = (body) => {
    this.method = "POST";
    this.body = body || {};
    return this;
  };

  put = (body) => {
    this.method = "PUT";
    this.body = body || {};
    return this;
  };

  get delete() {
    this.method = "DELETE";
    return this;
  }
}

const API_BASE_URL = window.env.REACT_APP_API;

const apis = {
  ip: `${API_BASE_URL}/`,
};

const endpoints = {
  auth: {
    login: () => `/auth/login`,
    logout: () => `/auth/logout`,
    send_reset_password: () => `/auth/send_reset_password`,
    reset_password: () => `/auth/reset_password`,
    get_encrypted_user: () => `auth/get_encrypted_user`,
  },
  support_calls: {
    get_support_calls_by_cust_active: () =>
      `/support_calls/support_calls_by_cust_active`,
    update_support_call: () => `/support_calls/update_support_call`,
    get_support_call_statuses: () => `/support_calls/get_support_call_statuses`,
    get_support_calls_by_cust_history: () =>
      `/support_calls/support_calls_by_cust_history`,
    get_support_call: () => `/support_calls/get_support_call`,
    add_new_comment: () => `/support_calls/new_comment`,
    update_comment_ext: () => `/support_calls/update_comment_ext`,
    new_support_call: () => `/support_calls/new_support_call`,
    add_attachment: () => `/support_calls/add_attachment`,
    delete_attachment: () => `/support_calls/delete_attachment`,
    get_user_customers: () => `/support_calls/get_user_customers`,
    get_types: () => `/support_calls/get_types`,
    severity_types: () => `/support_calls/severity_types`,
  },

  report_details: {
    get_user_report_details: () => `/report_details/get_user_report_details`,
    delete_report: () => `/report_details/delete_report`,
    add_report: () => `/report_details/add_report`,
    update_report: () => `/report_details/update_report`,
    get_report: () => `/report_details/get_report`,
    get_questions: () => `/report_details/get_questions`,
    get_calls_by_customer: () => `/report_details/get_calls_by_customer`,
    get_tasks_by_customer: () => `/report_details/get_tasks_by_customer`,
  },

  month_reports: {
    get_user_month_reports: () => `/month_reports/get_user_month_reports`,
    get_month_reports_statuses: () =>
      `/month_reports/get_month_reports_statuses`,
    delete_expense: () => `/month_reports/delete_expense`,
    add_expense: () => `/month_reports/add_expense`,
    update_expense: () => `/month_reports/update_expense`,
    update_report: () => `/month_reports/update_report`,
    add_report: () => `/month_reports/add_report`,
    update_reports: () => `/month_reports/update_reports`,
    get_abs: () => `/month_reports/get_abs`,
    get_gls: () => `/month_reports/get_gls`,
  },

  tasks: {
    get_tasks: () => `/tasks/get_tasks`,
    get_task: () => `/tasks/get_task`,
    get_task_ext: () => `tasks/get_task_ext`,
    get_types: () => `/tasks/get_types`,
    get_users: () => `/tasks/get_users`,
    new_task: () => `/tasks/new_task`,
    add_new_comment: () => `/tasks/new_comment`,
    get_statuses: () => `/tasks/get_statuses`,
    get_available_statuses: () => `/tasks/get_available_statuses`,
    add_attachment: () => `/tasks/add_attachment`,
    delete_attachment: () => `/tasks/delete_attachment`,
    update_task: () => `/tasks/update_task`,
    get_tasks_base_filter: () => `/tasks/get_tasks_base_filter`,
  },

  customers_connections: {
    get_customer_connections: () =>
      `/customers_connections/get_customer_connections`,
    add_customer_connection: () =>
      `/customers_connections/add_customer_connection`,
    update_customer_connection: () =>
      `/customers_connections/update_customer_connection`,
    delete_customer_connection: () =>
      `/customers_connections/delete_customer_connection`,
    get_connection_types: () => `/customers_connections/get_connection_types`,
  },

  common: {
    get_customers: () => `/common/get_customers`,
    get_customer_orders: () => `/common/get_customer_orders`,
    get_pdf_report: () => `/common/get_pdf_report`,
  },
};

const fetcher = () => {
  const sessionStorageUser = JSON.parse(
    localStorage.getItem("coralis-support-user")
  );
  const token = sessionStorageUser?.userDetails?.accessToken;

  return new Fetcher(apis, endpoints).setOptions({
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    },
  });
};

export default fetcher;
